import omitDeep from "omit-deep-lodash";
import { v4 } from "uuid";
import { Notify } from "@digitallab/grid-common-components";

import {
  LOG_SHEET_BY_CLUSTER_ENTRY_REF_ID,
  ON_CREATE_DIGITAL_LAB_LOGBOOK_LOG_SHEET,
  ON_UPDATE_DIGITAL_LAB_LOGBOOK_LOG_SHEET,
  CREATE_DIGITAL_LAB_LOGBOOK_LOG_SHEET_CHANGE,
  CREATE_DIGITAL_LAB_LOGBOOK_LOG_SHEET_CLUSTER_CHANGE
} from "../../../../gql/logBooksapi";
import { getAllData, waitUntilSubscribeConditionTimes } from "../../../../utils/helpers/fetching";
import { omitOrReturn, omitOrReturnArray, parseTime } from "../../../../utils/helpers/text";
import { LogStatus } from "../../enums";
import { changeDateFormat, toUtcDateTime } from "../../helpers";
import { getSubEquipmentStructure } from "../../run-logs/run-logs-form/CreateRunLogChange";
import { entryType } from "../../../../constants";

export const checkSubscribeCondition = (variables, dataProperty) => {
  return (data) => data[dataProperty].id === variables.logSheetEntryId;
};

const getUpdateSoftwareVersionOfPublishing = (theUpdateSoftwareVersion) => {
  if (theUpdateSoftwareVersion?.newValue) {
    return Boolean(theUpdateSoftwareVersion?.shouldBePublished);
  } else {
    return null;
  }
};

export const CreateLogSheetChange = async (logSheet, { client, isNew, equipmentDetail, isProcessingApproval }) => {
  try {
    delete logSheet.gxpReady?.site;
    delete logSheet.systemStatus?.site;
    const { actionDate } = logSheet;
    if (!actionDate){
      throw new Error('Action date is required.');
    }
    if (!(actionDate instanceof Date) && typeof actionDate !== "string") {
      throw new Error('actionDate is not a date object or date string.')
    }
    const variables = {
      ...logSheet,
      id: v4(),
      logSheetEntryId: isNew ? v4() : equipmentDetail.id,
      inventoryId: logSheet.inventoryId || null,
      modelVersion: "3",
      equipmentModel: logSheet.equipmentModel || equipmentDetail?.equipmentModel,
      materialNumber: logSheet.materialNumber || equipmentDetail?.materialNumber,
      logStatus: LogStatus.ACTIVE,
      description: logSheet.description || null,
      actionDate: actionDate instanceof Date ? changeDateFormat(actionDate) : actionDate,
      actionDateTime: toUtcDateTime(
        actionDate instanceof Date ? actionDate : new Date(actionDate),
        parseTime(logSheet.actionTime, { timeFormat: "HH:mm:ss", parseFormat: "hh:mm a" }) || "00:00:00"
      ),
      isApproved: Boolean(isProcessingApproval),
      actionTime:
        parseTime(logSheet.actionTime, {
          timeFormat: "HH:mm:ss",
          parseFormat: "hh:mm a"
        }) || null,
      action: { key: logSheet.action, value: logSheet.action },
      logbookConfigurationDetails: omitOrReturn(logSheet.logbookConfigurationDetails),
      logbookConfigurationBaseline: omitOrReturn(logSheet.logbookConfigurationBaseline),
      gxpReady: omitOrReturn(logSheet.gxpReady) || null,
      externalDocument: omitOrReturnArray(logSheet.externalDocument) || null,
      systemStatus: omitOrReturn(logSheet.systemStatus) || null,
      updatedSoftwareVersion: {
        ...omitOrReturn(logSheet.updatedSoftwareVersion),
        shouldBePublished: getUpdateSoftwareVersionOfPublishing(logSheet.updatedSoftwareVersion)
      }
    };
    const response = await client.mutate({
      mutation: CREATE_DIGITAL_LAB_LOGBOOK_LOG_SHEET_CHANGE,
      fetchPolicy: "no-cache",
      variables
    });

    if (isNew) {
      await waitUntilSubscribeConditionTimes(
        client,
        ON_CREATE_DIGITAL_LAB_LOGBOOK_LOG_SHEET,
        checkSubscribeCondition(variables, "onCreateDigitalLabLogbookLogSheet"),
        3
      );
    } else {
      await waitUntilSubscribeConditionTimes(
        client,
        ON_UPDATE_DIGITAL_LAB_LOGBOOK_LOG_SHEET,
        checkSubscribeCondition(variables, "onUpdateDigitalLabLogbookLogSheet"),
        3
      );
    }

    Notify({
      title: "",
      icon: "circle_confirm",
      type: "success",
      text: isProcessingApproval ? "Action log has been successfully approved." : "Successfully saved action log!"
    });
    return response;
  } catch (error) {
    Notify({
      title: "",
      icon: "no",
      type: "error",
      text: isProcessingApproval
        ? "Failed to approve Action Log, please try again later."
        : "Failed to save action log!"
    });

    return { error };
  }
};

export const CreateLogSheetChangeCluster = async (
  logSheet,
  { client, isNew, selectedEquipments, equipmentDetail, selectedOnlyParents = [] }
) => {
  // create gxpReady and systemStatus from Cluster values (for parent instrument)
  logSheet.gxpReady = logSheet.gxpReady
    ? { ...logSheet.gxpReady, shouldBePublished: logSheet.gxpReadyCluster.shouldBePublished || false }
    : null;
  logSheet.systemStatus = logSheet.systemStatus
    ? { ...logSheet.systemStatus, shouldBePublished: logSheet.systemStatusCluster.shouldBePublished || false }
    : null;
  try {
    const actionDate =
      logSheet.actionDate instanceof Date ? changeDateFormat(logSheet.actionDate) : logSheet.actionDate;
    const inputArray = generateInputVariable({
      selectedEquipments: [...selectedEquipments, ...selectedOnlyParents],
      logSheet,
      isNew,
      actionDate,
      equipmentDetail
    });

    const response = await client.mutate({
      mutation: CREATE_DIGITAL_LAB_LOGBOOK_LOG_SHEET_CLUSTER_CHANGE,
      fetchPolicy: "no-cache",
      variables: { input: omitDeep([...inputArray], "subEquipmentDetails") }
    });

    if (isNew) {
      await waitUntilSubscribeConditionTimes(
        client,
        ON_CREATE_DIGITAL_LAB_LOGBOOK_LOG_SHEET,
        checkSubscribeCondition(inputArray[0], "onCreateDigitalLabLogbookLogSheet"),
        3
      );
    } else {
      await waitUntilSubscribeConditionTimes(
        client,
        ON_UPDATE_DIGITAL_LAB_LOGBOOK_LOG_SHEET,
        checkSubscribeCondition(inputArray[0], "onUpdateDigitalLabLogbookLogSheet"),
        3
      );
    }
    if (response?.data?.createDigitalLabLogbookClusterLogSheetChange) {
      Notify({
        title: "",
        icon: "circle_confirm",
        type: "success",
        text: "Successfully saved action log!"
      });
      return response;
    }
  } catch (error) {
    Notify({
      title: "",
      icon: "no",
      type: "error",
      text: "Failed to save action log!"
    });
    return { error };
  }
};

/**
 * helper function to determine shouldBePublished value for attributes taht needs to be published
 * @param {object} equipmentDetail equipmentDetail object
 * @param {string} key name of the key to check
 * @returns {boolean} shouldBePublished value
 */
const checkForShouldBePublished = (equipmentDetail, key) => {
  return typeof equipmentDetail?.[key]?.shouldBePublished === "boolean"
    ? equipmentDetail?.[key]?.shouldBePublished
    : true;
};

/**
 * function to add system status cluster object if not present
 * @param {object} logSheet logSheet object
 * @returns {object} system status cluster object
 */
const addClusterItem = (logSheet, name) => {
  if (!logSheet[name]?.key) {
    logSheet[name].key = "";
    logSheet[name].value = "";
  }
  return omitOrReturn(logSheet?.[name]);
};

/**
 * function to get action log object structure from equipmentDetail and selectedEquipments.
 * It populates values needed for creaton action logs , including writing shouldBePublished
 * based on existing and changed values.
 * @param {object} param0
 * @returns {object} action log object structure
 */
const getActionLogDetailsStructure = ({ logSheet, isCluster = false, equipmentDetail, actionDate, isNew = false }) => {
  const instrumentDetails = {
    instrumentGxPStatus: equipmentDetail?.instrumentGxPStatus || null,
    equipmentNickName: equipmentDetail?.equipmentNickName || null,
    responsiblePerson: equipmentDetail?.responsiblePerson || null,
    manufacturer: equipmentDetail?.manufacturer || null,
    softwareVersion: equipmentDetail?.softwareVersion || null,
    location: equipmentDetail?.location || null,
    responsibleProxy: equipmentDetail?.responsibleProxy || null
  };

  if (isCluster) {
    instrumentDetails.configurationBaseline = equipmentDetail?.configurationBaseline || null;
  }
  if (isCluster) {
    delete logSheet?.gxpReady?.site;
    delete logSheet?.systemStatus?.site;
  } else {
    delete equipmentDetail?.gxpReady?.site;
    delete equipmentDetail?.systemStatus?.site;
  }
  let actionLogObj = {
    action: { key: logSheet?.action, value: logSheet?.action },
    actionDate: actionDate || null,
    actionDateTime: actionDate
      ? toUtcDateTime(
          new Date(actionDate),
          parseTime(logSheet.actionTime, { timeFormat: "HH:mm:ss", parseFormat: "hh:mm a" }) || "00:00:00"
        )
      : null,
    actionTime:
      parseTime(logSheet.actionTime, {
        timeFormat: "HH:mm:ss",
        parseFormat: "hh:mm a"
      }) || null,
    approverId: logSheet?.approverId || null,
    approverUserName: logSheet?.approverUserName || "",
    description: logSheet?.description || null,
    ecrNumber: logSheet?.ecrNumber,
    equipmentId: isCluster ? logSheet?.equipmentId : equipmentDetail?.equipmentId || null,
    editReason: logSheet?.editReason || "",
    editComment: logSheet?.editComment || "",
    gxpReady: isCluster
      ? omitOrReturn(logSheet?.gxpReady)
      : {
          ...omitOrReturn(equipmentDetail?.gxpReady),
          shouldBePublished: checkForShouldBePublished(equipmentDetail, "gxpReady")
        } || null,
    gxpReadyCluster: isCluster
      ? addClusterItem(logSheet, "gxpReadyCluster")
      : omitOrReturn(equipmentDetail?.gxpReady) || null,
    id: v4(),
    instrumentDetails,
    equipmentModel: isCluster ? logSheet?.equipmentModel : equipmentDetail?.equipmentModel || null,
    inventoryId: isCluster ? logSheet?.inventoryId : equipmentDetail?.inventoryId || null,
    logSheetEntryId: isNew ? v4() : isCluster ? equipmentDetail?.id : equipmentDetail?.logSheetEntryId || v4(),
    logStatus: LogStatus.ACTIVE,
    materialNumber: isCluster ? logSheet?.materialNumber : equipmentDetail?.materialNumber || "",
    modelVersion: "3",
    operatorId: logSheet?.operatorId,
    operatorUserName: logSheet?.operatorUserName,
    serialNumber: isCluster ? logSheet?.serialNumber : equipmentDetail?.serialNumber || "",
    isClusterQualificationChanged:
      equipmentDetail?.addInfo?.currentGxPStatusFromLogs && equipmentDetail?.addInfo?.currentSystemStatusFromLogs
        ? !logSheet?.isClusterQualificationChanged
        : true,
    site: logSheet?.site || null,
    systemStatus: isCluster
      ? omitOrReturn(logSheet?.systemStatus)
      : {
          ...omitOrReturn(equipmentDetail?.systemStatus),
          shouldBePublished: checkForShouldBePublished(equipmentDetail, "systemStatus")
        } || null,
    systemStatusCluster: isCluster
      ? addClusterItem(logSheet, "systemStatusCluster")
      : omitOrReturn(equipmentDetail?.systemStatus) || null,
    updatedSoftwareVersion: omitOrReturn(logSheet?.updatedSoftwareVersion),
    externalDocument: omitOrReturnArray(logSheet?.externalDocument),
    attachments: omitOrReturnArray(logSheet?.attachments)
  };
  attachAttribute(actionLogObj, logSheet, isCluster, equipmentDetail, "softwareVersion");
  attachAttribute(actionLogObj, logSheet, isCluster, equipmentDetail, "configurationBaseline");
  attachAttribute(actionLogObj, logSheet, isCluster, equipmentDetail, "configurationDetails");

  if (equipmentDetail?.entryType !== entryType?.cluster) {
    delete actionLogObj.gxpReadyCluster;
    delete actionLogObj.systemStatusCluster;
  }
  if (actionLogObj.softwareVersion && actionLogObj.updatedSoftwareVersion?.newValue === null) {
    actionLogObj.updatedSoftwareVersion = {
      newValue: actionLogObj.softwareVersion?.value,
      shouldBePublished: actionLogObj.softwareVersion?.shouldBePublished
    };
  }
  if (actionLogObj.configurationDetails && !actionLogObj.logbookConfigurationDetails) {
    actionLogObj.logbookConfigurationDetails = {
      newValue: actionLogObj.configurationDetails?.value,
      shouldBePublished: actionLogObj.configurationDetails?.shouldBePublished
    };
  }
  if (actionLogObj.configurationBaseline && !actionLogObj.logbookConfigurationBaseline) {
    actionLogObj.logbookConfigurationBaseline = {
      newValue: actionLogObj.configurationBaseline?.value,
      shouldBePublished: actionLogObj.configurationBaseline?.shouldBePublished
    };
  }
  return actionLogObj;
};

/**
 * function to attach attribute to action log object structure
 * @param {object} actionLogObj
 * @param {object} logSheet
 * @param {boolean} isCluster
 * @param {object} equipmentDetail
 * @param {string} key
 * @returns {void}
 */
const attachAttribute = (actionLogObj, logSheet, isCluster, equipmentDetail, key, actrionKey = null) => {
  if (equipmentDetail?.[key] && typeof equipmentDetail[key] === "object" && "key" in equipmentDetail[key]) {
    actionLogObj[key] = isCluster
      ? omitOrReturn(logSheet?.[key])
      : {
          ...omitOrReturn(equipmentDetail?.[key]),
          shouldBePublished: checkForShouldBePublished(equipmentDetail, key)
        } || null;
  } else if (typeof equipmentDetail?.[key] === "string") {
    actionLogObj[key] = {
      key: equipmentDetail[key],
      value: equipmentDetail[key],
      shouldBePublished: checkForShouldBePublished(equipmentDetail, key)
    };
  }
};

const generateInputVariable = ({ selectedEquipments, logSheet, isNew, actionDate, equipmentDetail }) => {
  let variables = [];
  let clusterObj = getActionLogDetailsStructure({
    equipmentDetail,
    logSheet,
    isNew,
    actionDate,
    isCluster: true
  });
  clusterObj = {
    ...clusterObj,
    subEquipment: getSubEquipmentStructure(selectedEquipments)
  };
  variables.push(clusterObj);
  selectedEquipments.forEach((subEquipment) => {
    let subEquipmentObj = {
      ...getActionLogDetailsStructure({
        equipmentDetail: subEquipment,
        logSheet,
        isNew,
        actionDate
      })
    };

    delete subEquipmentObj.id;
    delete subEquipmentObj.isClusterQualificationChanged;

    subEquipmentObj = {
      ...subEquipmentObj,
      clusterEntryRefId: clusterObj?.logSheetEntryId
    };
    variables.push(subEquipmentObj);
  });

  return variables;
};

export const getActionLogSubEquipments = async ({ client, id }) => {
  const subEquipmentData = await getAllData({
    client,
    query: LOG_SHEET_BY_CLUSTER_ENTRY_REF_ID,
    variables: {
      limit: 1000,
      clusterEntryRefId: id
    },
    dataPath: ["data", "logSheetByClusterEntryRefId"],
    drillData: true
  });
  return subEquipmentData?.items;
};
